import React, { useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { GlobalContext } from '../../contexts/GlobalContext';
import axios from '../../utils/axiosConfig';
import TutorialTooltip from '../DailyDigest/components/TutorialTooltip';
import TutorialRestartButton from '../TutorialRestartButton';
import BehavioralEditModal from './BehavioralEditModal';
import { TUTORIAL_STEPS } from './config/tutorialConfig';
import MockBehavioralInterview from './MockBehavioralInterview';
import LockedFeatureModal from '../common/LockedFeatureModal';

const BehavioralPrep = () => {
  const [mockInt, setMockInt] = useState(true);
  const [behavioralAnswers, setBehavioralAnswers] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [currentTutorialStep, setCurrentTutorialStep] = useState(0);
  const { userProfile, updateTutorialFlag, trialStatus, trialLoading } = useContext(GlobalContext);

  // Add refs for tutorial nodes
  const nodeRefs = {
    'behavioral-answers': useRef(null),
    'add-question': useRef(null),
    'mock-interview': useRef(null),
  };

  useEffect(() => {
    if (userProfile && userProfile.onboarding_progress) {
      setShowTutorial(userProfile.onboarding_progress.hasSeenBehavioralPrepTutorial !== true);
    }
  }, [userProfile]);

  const canUseAIInterviews = trialStatus ? trialStatus.can_use_ai_interviews : true;

  // Add effect to handle body scroll during tutorial
  useEffect(() => {
    if (showTutorial) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showTutorial]);

  const handleNextStep = () => {
    setCurrentTutorialStep((prev) => prev + 1);
  };

  const handleTutorialComplete = () => {
    setShowTutorial(false);
    updateTutorialFlag('hasSeenBehavioralPrepTutorial', true);
  };

  const fetchQuestions = async () => {
    try {
      console.log('Pulling behavioral questions');
      const userID = localStorage.getItem('userID');

      // Skip API call if user is not logged in
      if (!userID || userID === 'null' || userID === 'undefined') {
        console.log('User not logged in, skipping fetch questions');
        return;
      }

      const response = await axios.get(`/api/v1/answer/user/${userID}`);

      const data = response.data;
      console.log(data);
      console.log('That was the stuff');

      if (data.answers) {
        // Log the raw answers array
        console.log('Raw answers from /answer/user:', data.answers);

        const answersWithQuestions = await Promise.all(
          data.answers.map(async (answer) => {
            // Fetch the question details for this answer
            const questionResponse = await axios.get(`/api/v1/question/${answer.question_id}`);

            // Detailed logging of the question response structure
            console.log(`Question response for id ${answer.question_id}:`, questionResponse.data);

            // The backend returns something like:
            // {
            //   question: {
            //     question_id: 4,
            //     question: "Tell me ...",
            //     is_default: true
            //   }
            // }
            // But the UI expects qa.question.question_text and qa.answer_text.
            // Normalise the data into the expected shape here.

            const questionObjFromApi = questionResponse.data?.question ?? {};

            return {
              ...answer,
              // Normalised answer field expected by the UI
              answer_text: answer.answer ?? answer.answer_text ?? '',
              // Normalised question object expected by the UI
              question: {
                ...questionObjFromApi,
                question_text:
                  questionObjFromApi.question ?? questionObjFromApi.question_text ?? '',
              },
            };
          })
        );

        // Log the merged answer+question objects
        console.log('Mapped answersWithQuestions:', answersWithQuestions);

        setBehavioralAnswers(answersWithQuestions);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  useEffect(() => {
    console.log('Updated behavioralAnswers:', behavioralAnswers);
  }, [behavioralAnswers]); // This useEffect will log the updated state\\

  const onCreateAnswer = async (placeholder, answer, question) => {
    // Create the question first
    try {
      const response = await axios.post('/api/v1/question/create', {
        is_default: false,
        question: question,
      });

      const responseData = response.data;
      console.log('Successfully created it');

      // Create the answer to this question
      const responseAnswer = await axios.post('/api/v1/answer/create', {
        user_id: localStorage.getItem('userID'),
        question_id: responseData.question_id,
        answer: answer,
      });

      console.log('Successfully created answer');
      fetchQuestions();
    } catch (error) {
      console.log('Failed to create new behavioral answer', error);
    }
  };

  const onSave = async (answerId, answer, newQuestion) => {
    try {
      const response = await axios.put(`/api/v1/answer/${answerId}`, {
        answer,
      });

      const data = response.data;
      console.log(data);

      // Call the fetchQuestions function after the save is successful
      fetchQuestions();
    } catch (error) {
      console.error("Something's wrong", error);
    }
  };

  const addDefaultQuestions = async () => {
    try {
      const userID = localStorage.getItem('userID');

      // Store all fetch promises in an array
      const promises = [];

      // Loop through question IDs 1 to 5
      for (let questionID = 1; questionID <= 5; questionID++) {
        const request = axios.post('/api/v1/answer/create', {
          user_id: userID,
          question_id: questionID, // Using the current questionID in the loop
          answer: 'Edit your answer here!',
        });
        promises.push(request);
      }

      // Wait for all questions to be added before calling fetchQuestions
      await Promise.all(promises);
      console.log('All questions added successfully, fetching questions...');

      // Call fetchQuestions after all API requests are done
      fetchQuestions();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const exportAnswers = () => {
    if (behavioralAnswers.length === 0) {
      alert('No behavioral answers to export. Please add some questions and answers first.');
      return;
    }

    // Format the answers for export
    let exportContent = 'BEHAVIORAL INTERVIEW ANSWERS\n';
    exportContent += '================================\n\n';

    behavioralAnswers.forEach((qa, index) => {
      exportContent += `${index + 1}. QUESTION: ${qa.question.question_text}\n\n`;
      exportContent += `ANSWER:\n${qa.answer_text || 'No answer provided yet.'}\n\n`;
      exportContent += '---\n\n';
    });

    exportContent += `\nExported on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}\n`;
    exportContent += 'Generated by InternHub Behavioral Interview Prep\n';

    // Create and download the file
    const blob = new Blob([exportContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `behavioral-interview-answers-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Add delete function for behavioral answers
  const onDelete = async (answerId) => {
    if (
      !window.confirm(
        'Are you sure you want to delete this question and answer? This action cannot be undone.'
      )
    ) {
      return;
    }

    try {
      const response = await axios.delete(`/api/v1/answer/${answerId}`);

      console.log('Successfully deleted behavioral answer');
      // Refresh the questions list
      fetchQuestions();
    } catch (error) {
      console.error('Failed to delete behavioral answer', error);
      alert('Failed to delete the question. Please try again.');
    }
  };

  const [searchTerm, setSearchTerm] = useState('');

  const RobloxQuestions = [
    {
      title: 'Why do you want to work at Roblox?',
      subtitle: 'Company-Specific',
      content:
        "I've always admired Roblox's commitment to fostering creativity and building a global community. I believe my skills and experiences align well with Roblox's mission and values.",
      link: '#',
    },
    {
      title: 'What interests you about the role at Roblox?',
      subtitle: 'Company-Specific',
      content:
        "What interests me most about this role at Roblox is the opportunity to work on a platform that is shaping the future of entertainment. I'm excited about the impact I could have.",
      link: '#',
    },
    {
      title: 'How would you handle a disagreement with a team member?',
      subtitle: 'Behavioral',
      content: '',
      link: '#',
    },
    {
      title: 'Describe a time when you faced a significant obstacle.',
      subtitle: 'Behavioral',
      content: '',
      link: '#',
    },
    // Add more questions as needed
  ];

  const BloombergQuestions = [
    {
      title: 'Why do you want to work at Bloomberg?',
      subtitle: 'Company-Specific',
      content:
        "I've always admired Bloomberg's commitment to delivering business and market news, data, analysis, and video to the world. I believe my skills and experiences align well with Bloomberg's mission and values.",
      link: '#',
    },
    {
      title: 'What interests you about the role at Bloomberg?',
      subtitle: 'Company-Specific',
      content:
        "What interests me most about this role at Bloomberg is the opportunity to work on products that reach millions of people in the financial world. I'm excited about the impact I could have.",
      link: '#',
    },
    {
      title: 'Tell me about a time when you had to make a difficult decision.',
      subtitle: 'Behavioral',
      content: '',
      link: '#',
    },
    {
      title: 'Describe a project that you are particularly proud of.',
      subtitle: 'Behavioral',
      content: '',
      link: '#',
    },
    // Add more questions as needed
  ];
  const companyData = [
    { name: 'Roblox', questions: RobloxQuestions },
    { name: 'Bloomberg', questions: BloombergQuestions },
  ];

  const options = companyData.map((company, index) => ({
    value: index,
    label: company.name,
  }));
  const [selectedCompany, setSelectedCompany] = useState(0);
  console.log(options);
  console.log(selectedCompany);
  console.log(companyData[selectedCompany].questions);

  const QuestionAnswers = [
    {
      title: 'Tell me about yourself',
      subtitle: 'Introduction',
      content:
        'I am a software engineer with 5 years of experience in the industry. I specialize in front-end development, particularly with React and Redux. I love problem-solving and thrive in team environments.',
      link: '#',
    },
    {
      title: 'Tell me about a time you dealt with conflict',
      subtitle: 'Case Study',
      content:
        'Once, my team and I were having a disagreement. Luckily, I was able to step up and my leadership skills allowed me to save the day.',
      link: '#',
    },
    {
      title: 'What are your weaknesses?',
      subtitle: 'Self-Assessment',
      content: '',
      link: '#',
    },
    {
      title: 'What are your strengths?',
      subtitle: 'Self-Assessment',
      content:
        'One of my key strengths is my communication skills. I work very well in teams and have always been able to establish good rapport with my colleagues. I also have strong coding skills and a deep understanding of data structures and algorithms.',
      link: '#',
    },
    {
      title: 'Tell me about yourself',
      subtitle: 'Introduction',
      content:
        'I am a software engineer with 5 years of experience in the industry. I specialize in front-end development, particularly with React and Redux. I love problem-solving and thrive in team environments.',
      link: '#',
    },
    {
      title: 'Tell me about a time you dealt with conflict',
      subtitle: 'Case Study',
      content:
        'Once, my team and I were having a disagreement. Luckily, I was able to step up and my leadership skills allowed me to save the day.',
      link: '#',
    },
    {
      title: 'What are your weaknesses?',
      subtitle: 'Self-Assessment',
      content: '',
      link: '#',
    },
    {
      title: 'What are your strengths?',
      subtitle: 'Self-Assessment',
      content:
        'One of my key strengths is my communication skills. I work very well in teams and have always been able to establish good rapport with my colleagues. I also have strong coding skills and a deep understanding of data structures and algorithms.',
      link: '#',
    },
    // Add more questions as needed
  ];

  const Card = ({ title, content, link, answer }) => {
    const isContentEmpty = !content || content.trim() === '';
    const [isEditingAnswer, setIsEditingAnswer] = useState(false);
    const [isEditingQuestion, setIsEditingQuestion] = useState(false);
    const [editedContent, setEditedContent] = useState(content);
    const [editedQuestion, setEditedQuestion] = useState(title);

    // Function to clean text by removing extra whitespace
    const cleanText = (text) => {
      if (!text) return '';
      return text
        .replace(/\s+/g, ' ') // Replace multiple spaces/newlines with single space
        .trim(); // Remove leading/trailing whitespace
    };

    // Function to clean text while preserving newlines (for editing)
    const cleanTextPreserveNewlines = (text) => {
      if (!text) return '';
      return text
        .replace(/[ \t]+/g, ' ') // Replace multiple spaces/tabs with single space, but keep newlines
        .replace(/\n\s*\n\s*\n+/g, '\n\n') // Replace multiple consecutive newlines with double newlines
        .trim(); // Remove leading/trailing whitespace
    };

    const handleEditAnswer = () => {
      setIsEditingAnswer(true);
      setEditedContent(cleanTextPreserveNewlines(content)); // Preserve newlines for answer editing
    };

    const handleEditQuestion = () => {
      setIsEditingQuestion(true);
      setEditedQuestion(cleanTextPreserveNewlines(title)); // Preserve newlines for question editing
    };

    const handleSaveAnswer = () => {
      onSave(answer.answer_id, editedContent);
      setIsEditingAnswer(false);
    };

    const handleSaveQuestion = () => {
      onUpdateQuestion(answer.question_id, editedQuestion);
      setIsEditingQuestion(false);
    };

    const handleCancelAnswer = () => {
      setIsEditingAnswer(false);
      setEditedContent(content);
    };

    const handleCancelQuestion = () => {
      setIsEditingQuestion(false);
      setEditedQuestion(title);
    };

    const handleDelete = () => {
      onDelete(answer.answer_id);
    };

    return (
      <div className="group bg-white border border-gray-200 rounded-xl shadow-xs transition-all duration-300 hover:shadow-md">
        <div className="p-4 md:p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1 pe-3">
              {!isEditingQuestion ? (
                <h3 className="text-lg font-semibold text-gray-800 group-hover:text-blue-600 transition-colors">
                  {title}
                </h3>
              ) : (
                <div className="space-y-3">
                  <textarea
                    className="py-3 px-4 block w-full border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                    rows="3"
                    value={editedQuestion}
                    onChange={(e) => setEditedQuestion(e.target.value)}
                    placeholder="Enter your question here..."
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveQuestion}
                      type="button"
                      className="py-1.5 px-3 inline-flex items-center gap-x-1 text-xs font-medium rounded-lg border border-transparent bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none"
                    >
                      <svg
                        className="size-3"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                        <polyline points="17,21 17,13 7,13 7,21" />
                        <polyline points="7,3 7,8 15,8" />
                      </svg>
                      Save
                    </button>
                    <button
                      onClick={handleCancelQuestion}
                      type="button"
                      className="py-1.5 px-3 inline-flex items-center gap-x-1 text-xs font-medium rounded-lg border border-gray-200 bg-white text-gray-800 shadow-xs hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {!isEditingAnswer && !isEditingQuestion && (
              <div className="flex items-center gap-1">
                {/* Question edit button */}
                <button
                  onClick={handleEditQuestion}
                  type="button"
                  className="size-8 inline-flex justify-center items-center gap-x-2 text-sm font-semibold rounded-lg border border-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-800 disabled:opacity-50 disabled:pointer-events-none"
                  title="Edit question"
                >
                  <svg
                    className="size-4"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M7 7H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2v-1" />
                    <path d="M20.385 6.585a2.1 2.1 0 0 0-2.97-2.97L9 12v3h3l8.385-8.415z" />
                    <path d="M16 5l3 3" />
                  </svg>
                </button>
                {/* Delete button remains */}
                <button
                  onClick={handleDelete}
                  type="button"
                  className="size-8 inline-flex justify-center items-center gap-x-2 text-sm font-semibold rounded-lg border border-transparent text-red-500 hover:bg-red-100 hover:text-red-700 disabled:opacity-50 disabled:pointer-events-none"
                  title="Delete question"
                >
                  <svg
                    className="size-4"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="3,6 5,6 21,6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    <line x1="10" x2="10" y1="11" y2="17" />
                    <line x1="14" x2="14" y1="11" y2="17" />
                  </svg>
                </button>
              </div>
            )}
          </div>

          <div className="mt-4">
            {!isEditingAnswer ? (
              <>
                <div className="text-sm text-gray-600">
                  {isContentEmpty ? (
                    <p className="italic text-gray-400">
                      No answer provided yet. Click 'Answer' to add your response.
                    </p>
                  ) : (
                    <p className="leading-relaxed">{content}</p>
                  )}
                </div>

                {/* Answer / Edit button displayed under the content */}
                <div className="mt-3 flex justify-end">
                  <button
                    onClick={handleEditAnswer}
                    type="button"
                    className={`py-1.5 px-3 inline-flex items-center gap-x-1 text-xs font-semibold rounded-lg border border-transparent ${
                      isContentEmpty
                        ? 'text-red-600 hover:text-red-700 hover:bg-red-50'
                        : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50'
                    } disabled:opacity-50 disabled:pointer-events-none`}
                  >
                    {isContentEmpty ? (
                      <>
                        <svg
                          className="size-3"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M5 12h14" />
                          <path d="M12 5v14" />
                        </svg>
                        Answer
                      </>
                    ) : (
                      <>
                        <svg
                          className="size-3"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M7 7H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2v-1" />
                          <path d="M20.385 6.585a2.1 2.1 0 0 0-2.97-2.97L9 12v3h3l8.385-8.415z" />
                          <path d="M16 5l3 3" />
                        </svg>
                        Edit Answer
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <textarea
                  className="py-3 px-4 block w-full border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                  rows="6"
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.target.value)}
                  placeholder="Enter your answer here..."
                  autoFocus
                />
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={handleSaveAnswer}
                    type="button"
                    className="py-1.5 px-3 inline-flex items-center gap-x-1 text-xs font-medium rounded-lg border border-transparent bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none"
                  >
                    <svg
                      className="size-3"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                      <polyline points="17,21 17,13 7,13 7,21" />
                      <polyline points="7,3 7,8 15,8" />
                    </svg>
                    Save Answer
                  </button>
                  <button
                    onClick={handleCancelAnswer}
                    type="button"
                    className="py-1.5 px-3 inline-flex items-center gap-x-1 text-xs font-medium rounded-lg border border-gray-200 bg-white text-gray-800 shadow-xs hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const TutorialOverlay = () => {
    if (!showTutorial) return null;

    const currentStep = TUTORIAL_STEPS[currentTutorialStep];
    const currentRef = nodeRefs[currentStep.id];
    const rect = currentRef?.current?.getBoundingClientRect();

    if (!rect) return null;

    const getTooltipPosition = () => {
      const windowWidth = window.innerWidth;
      const isSmallScreen = windowWidth < 1024;

      if (currentStep.id === 'behavioral-answers') {
        return {
          top: rect.top + rect.height / 2 - 100,
          left: rect.right + 20,
          width: '300px',
          '--arrow-orientation': 'left',
        };
      }

      if (currentStep.id === 'add-question') {
        return {
          top: rect.top + rect.height / 2 - 100,
          left: rect.right + 20,
          width: '300px',
          '--arrow-orientation': 'left',
        };
      }

      if (currentStep.id === 'mock-interview') {
        return {
          top: rect.top + rect.height / 2 - 100,
          left: rect.left - 320,
          width: '300px',
          '--arrow-orientation': 'right',
        };
      }

      if (isSmallScreen) {
        return {
          top: rect.bottom + 20,
          left: rect.left,
          transform: 'none',
          width: Math.min(300, rect.width) + 'px',
        };
      }

      return {
        top: rect.top + rect.height / 2 - 100,
        left: currentStep.placement === 'left' ? rect.left - 320 : rect.right + 20,
        width: '300px',
      };
    };

    return (
      <>
        <div className="fixed inset-0 z-50">
          {rect && (
            <div
              className="absolute bg-transparent"
              style={{
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height,
                boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.7)',
                borderRadius: '0.5rem',
              }}
            />
          )}
        </div>

        <TutorialTooltip
          step={currentStep}
          steps={TUTORIAL_STEPS}
          onNext={handleNextStep}
          onComplete={handleTutorialComplete}
          style={getTooltipPosition()}
        />
      </>
    );
  };

  // Add function to update question text
  const onUpdateQuestion = async (questionId, newQuestionText) => {
    try {
      const response = await axios.put(`/api/v1/question/${questionId}`, {
        question: newQuestionText,
      });

      console.log('Successfully updated question text');
      // Refresh the questions list
      fetchQuestions();
    } catch (error) {
      console.error('Failed to update question text', error);
      alert('Failed to update the question. Please try again.');
    }
  };

  return (
    <div className="max-w-380 px-2 py-6 sm:px-4 lg:px-6 lg:py-8 mx-auto">
      {editModalOpen && (
        <BehavioralEditModal
          title={editModalOpen.question.question_text}
          body={editModalOpen.answer_text}
          onClose={() => {
            setEditModalOpen(null);
            console.log('hello');
          }}
          answer={editModalOpen}
          onSave={onSave}
        />
      )}
      {createModalOpen && (
        <BehavioralEditModal
          title={''}
          body={''}
          onClose={() => {
            setCreateModalOpen(false);
          }}
          answer={null}
          onSave={onCreateAnswer}
        />
      )}

      {/* Tutorial Overlay */}
      <TutorialOverlay />
      <TutorialRestartButton tutorialKey="hasSeenBehavioralPrepTutorial" />

      <div className="grid lg:grid-cols-2 gap-4 h-[calc(100vh-120px)]">
        {/* Column 1: General Questions */}
        <div
          ref={nodeRefs['behavioral-answers']}
          className="bg-white border border-gray-200 rounded-xl shadow-xs overflow-hidden"
        >
          <div className="p-4 md:p-5">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-gray-800 sm:text-3xl">
                Your behavioral interview answers
              </h1>
            </div>

            {/* Search and Action Bar */}
            <div className="mt-6 space-y-3">
              <div className="relative">
                <div className="absolute inset-y-0 inset-s-0 flex items-center pointer-events-none ps-4">
                  <svg
                    className="size-4 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                </div>
                <input
                  type="text"
                  className="py-3 ps-11 pe-4 block w-full border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 bg-white disabled:opacity-50 disabled:pointer-events-none"
                  placeholder="Search behavioral questions..."
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  ref={nodeRefs['add-question']}
                  type="button"
                  className="py-2 px-3 inline-flex items-center gap-x-2 text-sm font-semibold rounded-lg border border-transparent bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none"
                  onClick={() => setCreateModalOpen(true)}
                >
                  <svg
                    className="size-4"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 12h14" />
                    <path d="M12 5v14" />
                  </svg>
                  New question
                </button>
                <button
                  type="button"
                  className="py-2 px-3 inline-flex items-center gap-x-2 text-sm font-semibold rounded-lg border border-transparent bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:pointer-events-none"
                  onClick={exportAnswers}
                >
                  <svg
                    className="size-4"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7,10 12,15 17,10" />
                    <line x1="12" x2="12" y1="15" y2="3" />
                  </svg>
                  Export answers
                </button>
              </div>
            </div>

            {/* Questions List */}
            <div className="mt-5 space-y-4 overflow-y-auto max-h-[65vh]">
              {behavioralAnswers
                .filter((qa) => {
                  if (!qa?.question?.question_text) return false;
                  return qa.question.question_text.toLowerCase().includes(searchTerm.toLowerCase());
                })
                .sort((a, b) => {
                  return a.question.question_id - b.question.question_id;
                })
                .map((qa, index) => (
                  <Card
                    key={index}
                    title={qa.question.question_text}
                    content={qa.answer_text}
                    link={qa.link}
                    answer={qa}
                  />
                ))}

              {behavioralAnswers.length === 0 && (
                <div className="text-center py-8">
                  <img
                    src="/badges/kitty_idea.png"
                    alt="No questions yet"
                    className="mx-auto size-12"
                  />
                  <h3 className="mt-2 text-sm font-semibold text-gray-800">No questions yet</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Get started by creating your first behavioral question.
                  </p>
                  <button
                    onClick={addDefaultQuestions}
                    type="button"
                    className="mt-3 py-2 px-3 inline-flex items-center gap-x-2 text-sm font-semibold rounded-lg border border-gray-200 bg-white text-gray-800 shadow-xs hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
                  >
                    <svg
                      className="size-4"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 12h14" />
                      <path d="M12 5v14" />
                    </svg>
                    Add 5 default questions
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Column 2: Mock Interview Panel */}
        {mockInt && (
          <div
            ref={nodeRefs['mock-interview']}
            className="bg-gray-50 border border-gray-200 rounded-xl shadow-xs overflow-hidden"
          >
            <div className="h-full flex flex-col">
              {trialLoading ? (
                <div className="flex items-center justify-center p-8">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading interview access...</p>
                  </div>
                </div>
              ) : !canUseAIInterviews ? (
                <LockedFeatureModal
                  featureName="AI Behavioral Mock Interviews"
                  showAsInline={true}
                />
              ) : (
                <MockBehavioralInterview onQuestionsExported={fetchQuestions} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BehavioralPrep;
